import { useSyncExternalStoreWithSelector } from "use-sync-external-store/shim/with-selector";
import { deepEqual } from "fast-equals";
import {
  appStore,
  type AppStoreState,
  type ViewedProductState,
  type ViewedPageProperties,
} from "src/stores/app/app.store";
import type { ApolloQueryResult } from "@apollo/client";
import type { AddedToCartProduct } from "src/types/addedToCardProduct.d";
import type { FeatureFlags } from "src/types/featureFlags";
import type {
  UserPreferences,
  UserPreferencesByEmail,
} from "src/types/graphql.d";
import type { Money } from "src/types/ctgraphql.d";
import type { StoreSettingsData } from "src/hooks/useStoreSettings";
import { locale } from "src/config/app-config";
import { CONSENT_NOT_REQUIRED_LOCALES } from "src/molecules/ConsentMangerBanner/utils";

export function useAppStore<T>(
  selector: (state: AppStoreState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is
): T {
  return useSyncExternalStoreWithSelector(
    appStore.subscribe,
    appStore.getSnapshot,
    appStore.getServerSnapshot,
    selector,
    isEqual
  );
}

const selectAdyenMerchantId = (state: AppStoreState) => state.adyenMerchantId;
const selectUserPreferences = (state: AppStoreState) => state.userPreferences;
const selectAddedProdToCart = (state: AppStoreState) => state.addedProdToCart;
const selectCartMinLineItemsTotalPriceCentAmount = (state: AppStoreState) =>
  state.cartMinLineItemsTotalPriceCentAmount;
const selectCartMinLineItemsTotalPriceDiff = (state: AppStoreState) =>
  state.cartMinLineItemsTotalPriceDiff;
const selectRefetchStoreSettings = (state: AppStoreState) =>
  state.refetchStoreSettings;
const selectFeatureFlags = (state: AppStoreState) => state.featureFlags;
const selectCollection = (state: AppStoreState) => state.collection;
const selectViewedProductState = (state: AppStoreState) =>
  state.viewedProductState;
const selectDisplayConsentManager = (state: AppStoreState) =>
  state.displayConsentManager;
const selectShouldScrollToFooter = (state: AppStoreState) =>
  state.shouldScrollToFooter;
const selectHomePageMounted = (state: AppStoreState) => state.homePageMounted;
const selectGeoRedirectCardOpen = (state: AppStoreState) =>
  state.geoRedirectCardOpen;
const selectAppDownloadBannerOpen = (state: AppStoreState) =>
  state.appDownloadBannerOpen;
const selectSubscribeOverlayOpen = (state: AppStoreState) =>
  state.subscribeOverlayOpen;
const selectInitializedConsent = (state: AppStoreState) =>
  state.initializedConsent;
const selectPageViewedProperties = (state: AppStoreState) =>
  state.pageViewedProperties;
const selectConsentUpdated = (state: AppStoreState) => state.consentUpdated;
const selectSigningOut = (state: AppStoreState) => state.signingOut;

const selectCartMinLineItems = (state: AppStoreState) => ({
  cartMinLineItemsTotalPriceCentAmount:
    state.cartMinLineItemsTotalPriceCentAmount,
  cartMinLineItemsTotalPriceDiff: state.cartMinLineItemsTotalPriceDiff,
});

export function useAdyenMerchantId(): string {
  return useAppStore(selectAdyenMerchantId);
}

export function useUserPreferences():
  | UserPreferences
  | UserPreferencesByEmail
  | undefined {
  return useAppStore(selectUserPreferences);
}

export function useAddedProdToCart(): AddedToCartProduct | null | undefined {
  return useAppStore(selectAddedProdToCart);
}

export function useCartMinLineItemsTotalPriceCentAmount(): number | null {
  return useAppStore(selectCartMinLineItemsTotalPriceCentAmount);
}

export function useCartMinLineItemsTotalPriceDiff(): Money | null {
  return useAppStore(selectCartMinLineItemsTotalPriceDiff, deepEqual);
}

export function useCartMinLineItems() {
  return useAppStore(selectCartMinLineItems, deepEqual);
}

export function useRefetchStoreSettings():
  | (() => Promise<ApolloQueryResult<StoreSettingsData>>)
  | undefined {
  return useAppStore(selectRefetchStoreSettings);
}

export function useAppFeatureFlags(): FeatureFlags {
  return useAppStore(selectFeatureFlags, deepEqual);
}

export function useCollection(): { email: string } {
  return useAppStore(selectCollection, deepEqual);
}

export function useViewedProductState(): ViewedProductState {
  return useAppStore(selectViewedProductState);
}

export function useDisplayConsentManager(): boolean {
  return useAppStore(selectDisplayConsentManager);
}

/**
 * Whether the consent banner is on screen: it waits to be answered and this
 * store asks for consent at all. Overlays wait for it to close.
 */
export function useIsConsentManagerShown(): boolean {
  const displayConsentManager = useDisplayConsentManager();

  return (
    displayConsentManager && !CONSENT_NOT_REQUIRED_LOCALES.includes(locale)
  );
}

export function useShouldScrollToFooter(): boolean {
  return useAppStore(selectShouldScrollToFooter);
}

export function useHomePageMounted(): boolean {
  return useAppStore(selectHomePageMounted);
}

export function useGeoRedirectCardOpen(): boolean {
  return useAppStore(selectGeoRedirectCardOpen);
}

export function useAppDownloadBannerOpen(): boolean {
  return useAppStore(selectAppDownloadBannerOpen);
}

export function useSubscribeOverlayOpen(): boolean {
  return useAppStore(selectSubscribeOverlayOpen);
}

export function useInitializedConsent(): boolean {
  return useAppStore(selectInitializedConsent);
}

export function usePageViewedProperties(): ViewedPageProperties {
  return useAppStore(selectPageViewedProperties, deepEqual);
}

export function useConsentUpdated(): boolean {
  return useAppStore(selectConsentUpdated);
}

export function useSigningOut(): boolean {
  return useAppStore(selectSigningOut);
}

export function useAppActions() {
  return {
    setAdyenMerchantId: appStore.setAdyenMerchantId,
    setUserPreferences: appStore.setUserPreferences,
    setAddedProdToCart: appStore.setAddedProdToCart,
    setCartMinLineItemsTotalPriceCentAmount:
      appStore.setCartMinLineItemsTotalPriceCentAmount,
    setCartMinLineItemsTotalPriceDiff:
      appStore.setCartMinLineItemsTotalPriceDiff,
    setRefetchStoreSettings: appStore.setRefetchStoreSettings,
    setFeatureFlags: appStore.setFeatureFlags,
    setCollection: appStore.setCollection,
    setViewedProductState: appStore.setViewedProductState,
    setDisplayConsentManager: appStore.setDisplayConsentManager,
    setShouldScrollToFooter: appStore.setShouldScrollToFooter,
    setHomepageMounted: appStore.setHomepageMounted,
    setGeoRedirectCardOpen: appStore.setGeoRedirectCardOpen,
    setAppDownloadBannerOpen: appStore.setAppDownloadBannerOpen,
    setSubscribeOverlayOpen: appStore.setSubscribeOverlayOpen,
    setInitializedConsent: appStore.setInitializedConsent,
    setPageViewedProperties: appStore.setPageViewedProperties,
    setConsentUpdated: appStore.setConsentUpdated,
    setSigningOut: appStore.setSigningOut,
  };
}
